import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PagedTileGrid from '../profile/PagedTileGrid';
import { SendTile } from './SendTile';
import { sendsService } from '../../services/sends';
import { fromProfileState, type ProfileOrigin } from '../profile/profile-origin';
import type { Send } from '../../types/send';

interface UserSendsGridProps {
  userSlug: string;
  /** The profile the grid is on, which the climb pages it opens lead back to. */
  profile?: ProfileOrigin;
  className?: string;
}

/**
 * Everything a climber has sent, as a full-width grid of photos — newest
 * first, ten at a time, the next ten fetched as the bottom comes into view.
 * Each tile is a SendTile: the climb zoomed to its circled holds, with the
 * grade, the style of the send and the gym drawn over it.
 */
export default function UserSendsGrid({ userSlug, profile, className }: UserSendsGridProps) {
  const { t } = useTranslation();

  const fetchPage = useCallback(async (page: number) => {
    const response = await sendsService.getUserSends(userSlug, page);
    return { items: response.data, hasMore: response.meta.has_more ?? false };
  }, [userSlug]);

  return (
    <PagedTileGrid<Send>
      fetchPage={fetchPage}
      renderTile={(send) => <SendTile send={send} linkState={profile && fromProfileState(profile)} />}
      itemKey={(send) => send.id}
      emptyText={t('sends.noSends')}
      errorText={t('sends.loadError')}
      testId="user-sends-grid"
      className={className}
    />
  );
}
